import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import THEMES from '../data/coursEnSalle.json';
import {
  ArrowLeft, Search, BookOpen, Quote, Car, AlertTriangle, ListChecks,
  ChevronRight, Layers, Sparkles, Lightbulb,
} from 'lucide-react';

function ThemeContent({ t }) {
  if (!t) return null;
  return (
    <article className="space-y-6" data-testid={`theme-content-${t.num}`}>
      {/* Header thème */}
      <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-6 lg:p-8">
        <div className="flex items-start gap-5">
          <div className="shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-red-600 text-white font-serif-editorial text-[24px] lg:text-[30px] flex items-center justify-center shadow-lg shadow-red-900/40">
            {String(t.num).padStart(2, '0')}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1.5">Thème {t.num} / 57 · Support d&apos;animation en salle</div>
            <h2 className="font-serif-editorial text-white text-[24px] lg:text-[32px] leading-[1.05]">{t.title}</h2>
          </div>
        </div>
      </div>

      {/* Apports — Construction du savoir */}
      {t.apports && t.apports.length > 0 && (
        <section className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-7" data-testid="section-apports">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} className="text-red-400" />
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Apports · Construction du savoir</div>
          </div>
          <div className="space-y-3">
            {t.apports.map((a, i) => (
              <details key={i} className="group rounded-xl border border-white/10 bg-white/[0.02] open:border-red-700/30 open:bg-red-950/10 transition-all" open={i === 0}>
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 text-[14px] font-semibold text-white">
                  <span className="flex items-center gap-2">
                    <Sparkles size={13} className="text-red-400" />
                    {a.title || `Point ${i + 1}`}
                  </span>
                  <ChevronRight size={15} className="text-white/40 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-4 pb-4 space-y-2.5">
                  {a.parts.map((p, j) => {
                    if (p.type === 'p') {
                      return <p key={j} className="text-white/75 text-[13.5px] leading-relaxed">{p.text}</p>;
                    }
                    if (p.type === 'ul' || p.type === 'ol') {
                      const ListTag = p.type === 'ol' ? 'ol' : 'ul';
                      return (
                        <ListTag key={j} className={`${p.type === 'ol' ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 text-white/75 text-[13.5px] leading-relaxed marker:text-red-400`}>
                          {p.items.map((it, k) => <li key={k}>{it}</li>)}
                        </ListTag>
                      );
                    }
                    return null;
                  })}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Synthèse phrase clé */}
      {t.synthese && (
        <section className="rounded-2xl border-l-4 border-l-red-600 border-y border-r border-white/10 bg-gradient-to-r from-red-950/20 to-[#0d0d0e] p-5 lg:p-6" data-testid="section-synthese">
          <div className="flex items-start gap-3">
            <Quote size={20} className="text-red-400 shrink-0 mt-1" />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-2">Synthèse · phrase clé</div>
              <p className="text-white text-[16px] lg:text-[18px] leading-relaxed italic font-serif-editorial">« {t.synthese} »</p>
            </div>
          </div>
        </section>
      )}

      {/* Lien avec la conduite */}
      {t.lien_conduite && t.lien_conduite.length > 0 && (
        <section className="rounded-2xl border border-emerald-700/30 bg-emerald-950/10 p-5 lg:p-6" data-testid="section-lien">
          <div className="flex items-start gap-3">
            <Car size={18} className="text-emerald-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-300 font-bold font-mono-spec mb-2.5">Lien avec la conduite</div>
              <ul className="space-y-2">
                {t.lien_conduite.map((l, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-relaxed">
                    <ChevronRight size={13} className="text-emerald-400 shrink-0 mt-1" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 3 situations typiques */}
      {t.situations && t.situations.length > 0 && (
        <section className="rounded-2xl border border-sky-700/30 bg-sky-950/10 p-5 lg:p-6" data-testid="section-situations">
          <div className="flex items-start gap-3">
            <ListChecks size={18} className="text-sky-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-300 font-bold font-mono-spec mb-2.5">Situations typiques · gestion de groupe</div>
              <ul className="space-y-2">
                {t.situations.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-relaxed">
                    <Lightbulb size={13} className="text-sky-400 shrink-0 mt-1" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Pièges à éviter */}
      {t.pieges && t.pieges.length > 0 && (
        <section className="rounded-2xl border border-amber-700/30 bg-amber-950/10 p-5 lg:p-6" data-testid="section-pieges">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-amber-300 font-bold font-mono-spec mb-2.5">Pièges à éviter</div>
              <ul className="space-y-2">
                {t.pieges.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] leading-relaxed">
                    <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-1" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

export default function CoursEnSallePage() {
  const [activeNum, setActiveNum] = useState(1);
  const [q, setQ] = useState('');
  const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtered = useMemo(() => {
    if (!q.trim()) return THEMES;
    const nq = norm(q);
    return THEMES.filter((t) => norm(t.title).includes(nq));
  }, [q]);
  const active = THEMES.find((t) => t.num === activeNum);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="cours-en-salle-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Cours en salle · 57 thèmes</span>
        </nav>

        {/* Hero */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-7 lg:p-9 mb-7 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/12 blur-3xl" />
          <div className="relative z-10">
            <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.02] tracking-tight">
              Cours en salle<br />
              <span className="text-red-500">57 thèmes</span> sécurité routière
            </h1>
          </div>
        </div>

        {/* Grid : sidebar nav + content */}
        <div className="grid lg:grid-cols-[320px_1fr] gap-5">
          {/* Sidebar */}
          <aside className="space-y-3">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher un thème…"
                  data-testid="theme-search"
                  className="w-full h-10 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-red-500/50"
                />
              </div>
              <div className="text-[10.5px] uppercase tracking-wider text-white/40 font-bold font-mono-spec mt-3 mb-1 px-1">
                {filtered.length} / 57 thèmes
              </div>
            </div>
            <nav className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-2 lg:max-h-[70vh] lg:overflow-y-auto">
              <ul className="space-y-0.5">
                {filtered.map((t) => {
                  const isActive = t.num === activeNum;
                  return (
                    <li key={t.num}>
                      <button
                        onClick={() => setActiveNum(t.num)}
                        data-testid={`theme-link-${t.num}`}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-start gap-2.5 transition ${isActive ? 'bg-red-600 text-white shadow-md shadow-red-900/30' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                      >
                        <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[10.5px] font-bold font-mono-spec ${isActive ? 'bg-white/20 text-white' : 'bg-white/5 text-red-400'}`}>
                          {String(t.num).padStart(2, '0')}
                        </span>
                        <span className="text-[12.5px] leading-snug pt-0.5">{t.title}</span>
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="px-3 py-4 text-center text-white/40 text-[12.5px]">Aucun thème trouvé.</li>
                )}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <main className="min-w-0">
            <ThemeContent t={active} />
          </main>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 mt-10 pt-6 border-t border-white/5">
          <Link to="/cours-interactifs" data-testid="back-to-tools" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <ArrowLeft size={14} /> Tous les outils
          </Link>
          <div className="text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
            <Layers size={11} /> 57 thèmes sécurité routière · codevoiture.fr
          </div>
        </div>
      </section>
    </PageShell>
  );
}
